import React, { useState, useEffect, useRef } from 'react';
import { Video, Search, RefreshCw, Eye, Check, Play, Pause, Loader2, Info } from 'lucide-react';
import { useAppDemo } from '../../context/AppDemoContext';
import { useAnalytics } from '../../hooks/useAnalytics';
import toast from 'react-hot-toast';

/**
 * Video Selection slide for searching and choosing a splash video
 */
const VideoSelectionSlide = ({ userId, registerValidation, organization }) => {
    const {
        splashVideo,
        setSplashVideo,
        appName,
        primaryColor,
        setIsLoading,
        setLoadingMessage,
        videoSearchTerm,
        setVideoSearchTerm,
        brandDataFetched,
        contentCategories: contextContentCategories,
        videoQueries,
        setVideoQueries
    } = useAppDemo();

    const { trackEvent } = useAnalytics(userId);
    const [searchTerm, setSearchTerm] = useState(videoSearchTerm || '');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedVideo, setSelectedVideo] = useState(splashVideo);
    const [isSearching, setIsSearching] = useState(false);
    const [error, setError] = useState(null);
    const [playingVideoId, setPlayingVideoId] = useState(null);
    const [contentCategories, setContentCategories] = useState([]);
    const [contentCategoriesLoaded, setContentCategoriesLoaded] = useState(false);
    const videoRefs = useRef({});
    const initializedRef = useRef(false);

    // Initialize state from context when it changes
    useEffect(() => {
        if (videoSearchTerm) {
            setSearchTerm(videoSearchTerm);
        }
        if (splashVideo) {
            setSelectedVideo(splashVideo);
        }
        if (videoQueries) {
            // Find the most recent search query results
            const lastQuery = videoQueries[videoQueries.length - 1];
            if (lastQuery && lastQuery.results && lastQuery.results.length > 0) {
                setSearchResults(lastQuery.results);
            }
        }
    }, [videoSearchTerm, splashVideo, videoQueries]);

    // Use content categories as default search terms
    useEffect(() => {
        // Check if content categories are already available in context
        if (brandDataFetched && contextContentCategories && contextContentCategories.length > 0) {
            console.log('Using content categories from context:', contextContentCategories.length);
            setContentCategories(contextContentCategories);
            
            // Set the first category as the search term if not already set
            if (!searchTerm && !videoSearchTerm && contextContentCategories.length > 0) {
                const firstCategory = contextContentCategories[0].name;
                console.log(`Using first content category for video search: ${firstCategory}`);
                setSearchTerm(firstCategory);
                setVideoSearchTerm(firstCategory);
                
                // Perform initial search with default term
                searchVideos(firstCategory).catch(err => {
                    console.error('Initial video search failed:', err);
                    setError('Failed to perform initial search. Please try another term.');
                });
                
                toast.success('Video search populated from content categories');
            }
            
            setContentCategoriesLoaded(true);
            return;
        }
        
        // Only execute once on mount to avoid infinite loops
        const fetchContentCategories = async () => {
            // Use the domain to fetch content categories
            const domain = organization?.website || organization?.domain || '';
            
            if (!domain) {
                console.log('No domain available to fetch content categories');
                initializeDefaultSearch();
                return;
            }
            
            // Add organization details in console logs for debugging
            console.log('Organization details for video search:', {
                id: organization?.id || 'not available',
                name: organization?.name || 'not available',
                domain: organization?.domain || 'not available',
                website: organization?.website || 'not available'
            });
            
            // Normalize domain
            let normalizedDomain = domain.toLowerCase();
            if (normalizedDomain.startsWith('http')) {
                try {
                    const url = new URL(normalizedDomain);
                    normalizedDomain = url.hostname;
                } catch (e) {
                    console.error('Error parsing URL:', e);
                    normalizedDomain = normalizedDomain.replace(/^https?:\/\//, '').split('/')[0];
                }
            }
            normalizedDomain = normalizedDomain.replace(/^www\./, '');
            
            try {
                console.log(`Fetching content categories for: ${normalizedDomain}`);
                const response = await fetch(`/api/website-data/domain/${normalizedDomain}/content-categories`);
                
                if (response.ok) {
                    const data = await response.json();
                    // Log data summary without the full content to avoid console flooding
                    console.log('Content categories received:', {
                        status: 'ok',
                        dataType: Array.isArray(data) ? 'array' : typeof data,
                        length: Array.isArray(data) ? data.length : (data && typeof data === 'object' ? Object.keys(data).length : 'N/A')
                    });
                    
                    if (data && Array.isArray(data) && data.length > 0) {
                        setContentCategories(data);
                        
                        // Set the first category as the search term if not already set
                        if (!searchTerm && !videoSearchTerm && data.length > 0) {
                            const firstCategory = data[0].name;
                            console.log(`Using first content category for video search: ${firstCategory}`);
                            setSearchTerm(firstCategory);
                            setVideoSearchTerm(firstCategory);
                            
                            // Perform initial search with default term
                            searchVideos(firstCategory).catch(err => {
                                console.error('Initial video search failed:', err);
                                setError('Failed to perform initial search. Please try another term.');
                            });
                            
                            toast.success('Video search populated from content categories');
                        }
                        // Content categories exist but searchTerm or videoSearchTerm is already set
                        // No need to do anything else in this case, just use the existing search term
                    } else {
                        console.log('No content categories found, using default search term');
                        initializeDefaultSearch();
                    }
                } else {
                    console.warn(`Failed to fetch content categories: ${response.status}`);
                    initializeDefaultSearch();
                }
            } catch (error) {
                console.error('Error fetching content categories:', error);
                initializeDefaultSearch();
            } finally {
                setContentCategoriesLoaded(true);
            }
        };
        
        fetchContentCategories();
        // We intentionally keep dependencies limited to avoid infinite loops
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Initialize with default search term (fallback)
    const initializeDefaultSearch = () => {
        if (!searchTerm && !videoSearchTerm) {
            // First check if we already have some content categories to use
            if (contentCategories && contentCategories.length > 0) {
                const firstCategory = contentCategories[0].name;
                console.log(`Setting initial video search term to first content category: ${firstCategory}`);
                setSearchTerm(firstCategory);
                setVideoSearchTerm(firstCategory);
                
                // Perform initial search with this term
                searchVideos(firstCategory).catch(err => {
                    console.error('Initial video search failed:', err);
                    setError('Failed to perform initial search. Please try another term.');
                });
                return;
            }
            
            // Prioritize organization domain over name, but check for generic placeholders
            const domain = organization?.website || organization?.domain || '';
            let defaultTerm = '';
            
            if (domain && 
                !domain.toLowerCase().includes('your') && 
                !domain.toLowerCase().includes('example')) {
                try {
                    const url = new URL(domain.startsWith('http') ? domain : `https://${domain}`);
                    defaultTerm = url.hostname.replace(/^www\./, '').split('.')[0];
                } catch (e) {
                    defaultTerm = domain.replace(/^www\./, '').replace(/\..+$/, '');
                }
            }
            
            // Use organization name, only if it's not a generic placeholder
            if ((!defaultTerm || defaultTerm.length < 2) && 
                organization?.name && 
                !organization.name.toLowerCase().includes('your') && 
                !organization.name.toLowerCase().includes('organization')) {
                defaultTerm = organization.name;
            } else if (appName) {
                // Extract first meaningful word from app name
                const appWords = appName.split(' ');
                defaultTerm = appWords[0];
                
                // Skip generic words
                if (defaultTerm.toLowerCase().includes('your') || 
                    defaultTerm.toLowerCase().includes('app') ||
                    defaultTerm.toLowerCase() === 'the') {
                    defaultTerm = appWords.length > 1 ? appWords[1] : 'religious';
                }
            }
            
            // Fallback to a relevant default term if nothing good is found
            if (!defaultTerm || defaultTerm.length < 2 || 
                defaultTerm.toLowerCase().includes('your') || 
                defaultTerm.toLowerCase().includes('organization')) {
                defaultTerm = 'religious';
            }
            
            console.log(`Setting initial video search term to: ${defaultTerm}`);
            setSearchTerm(defaultTerm);
            setVideoSearchTerm(defaultTerm);
            
            // Perform initial search with default term
            searchVideos(defaultTerm).catch(err => {
                console.error('Initial video search failed:', err);
                setError('Failed to perform initial search. Please try another term.');
            });
        }
        setContentCategoriesLoaded(true);
    };

    // Set up selected video from splashVideo prop - improved to ensure persistence
    useEffect(() => {
        if (splashVideo) {
            console.log('VIDEO SELECTION: Initializing from context:', splashVideo.title);
            setSelectedVideo(splashVideo);
            initializedRef.current = true;
        }
    }, [splashVideo]); // Only depend on splashVideo to avoid infinite loops

    // Register validation when video selection changes
    useEffect(() => {
        if (registerValidation) {
            const isValid = !!selectedVideo;
            registerValidation('video-selection', isValid);
        }
    }, [selectedVideo, registerValidation]);

    // Load saved search results when component mounts
    useEffect(() => {
        if (videoQueries && videoQueries.length > 0) {
            // Find the most recent search query results
            const lastQuery = videoQueries[videoQueries.length - 1];
            if (lastQuery && lastQuery.results && lastQuery.results.length > 0) {
                setSearchResults(lastQuery.results);
                setSearchTerm(lastQuery.term || searchTerm);
            }
        }
    }, [videoQueries]);

    // Handle search input change
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    // Handle search form submission
    const handleSearchSubmit = (e) => {
        e.preventDefault();
        if (searchTerm.trim()) {
            searchVideos(searchTerm.trim());
        }
    };

    // Handle click on suggested search term
    const handleSuggestedSearch = (term) => {
        setSearchTerm(term);
        setVideoSearchTerm(term);
        toast.success(`Searching for videos: ${term}`);
        trackEvent('video_category_search', { category: term });
        searchVideos(term);
    };

    // Enhanced searchVideos function to save results to context
    const searchVideos = async (query) => {
        if (!query.trim()) return;
        
        setIsSearching(true);
        setError(null);
        
        try {
            const response = await fetch(`/api/videos/search?query=${encodeURIComponent(query)}`);
            
            if (!response.ok) {
                throw new Error(`API error: ${response.status}`);
            }
            
            const data = await response.json();
            
            if (data && data.videos && data.videos.length > 0) {
                const formattedResults = data.videos.map(video => ({
                    id: video.id,
                    title: video.title,
                    description: video.description,
                    thumbnailUrl: video.thumbnailUrl || video.thumbnail,
                    videoUrl: video.videoUrl,
                    author: video.author || video.channelTitle,
                    duration: video.duration
                }));
                
                // Update local state
                setSearchResults(formattedResults);
                
                // Save to context for persistence
                const newQuery = {
                    term: query,
                    timestamp: new Date().toISOString(),
                    results: formattedResults
                };
                
                setVideoQueries(prev => [...(prev || []), newQuery]);
                
                trackEvent('video_search', {
                    query: query,
                    resultCount: formattedResults.length
                });
            } else {
                setSearchResults([]);
                setError('No videos found for this search term. Try another keyword.');
            }
        } catch (err) {
            console.error('Error searching for videos:', err);
            setError(`Failed to search for videos: ${err.message}`);
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    };

    // Handle video selection - updated to make persistence more reliable
    const handleVideoSelect = (video) => {
        if (!video || !video.id) {
            console.error('Invalid video selected:', video);
            return;
        }
        
        // Make sure the video has all needed properties for persistence
        const enhancedVideo = {
            ...video,
            // Ensure these properties are present for ReviewSlide
            thumbnailUrl: video.thumbnailUrl || video.thumbnail || video.image,
            title: video.title || 'Selected Video',
            author: video.author || video.channelTitle || 'Unknown Creator',
        };
        
        console.log('VIDEO SELECTION: Selected video with data:', enhancedVideo);
        
        // Update both local state and context
        setSelectedVideo(enhancedVideo);
        setSplashVideo(enhancedVideo);
        
        // Log to confirm context is updated
        console.log('VIDEO SELECTION: Updated splashVideo in context');
        
        // Show selected status message
        toast.success('Video selected for your app!');
    };

    // Handle playing video
    const toggleVideoPlay = (video) => {
        if (!video || !video.videoUrl || !video.id) {
            console.error('Cannot play video, missing required properties:', video);
            toast.error('Sorry, this video cannot be played');
            return;
        }
        
        // Get video element
        const videoElement = videoRefs.current[video.id];
        if (!videoElement) {
            console.error('Video element not found for:', video.id);
            return;
        }
        
        try {
            // If this video is already playing, pause it
            if (playingVideoId === video.id) {
                videoElement.pause();
                setPlayingVideoId(null);
                return;
            }
            
            // If another video is playing, pause it first
            if (playingVideoId && videoRefs.current[playingVideoId]) {
                videoRefs.current[playingVideoId].pause();
            }
            
            // Reset video to beginning if it was previously played
            videoElement.currentTime = 0;
            
            // Play the video
            videoElement.play().then(() => {
                setPlayingVideoId(video.id);
            }).catch(err => {
                console.error('Error playing video:', err);
                toast.error('Could not play the video');
            });
        } catch (err) {
            console.error('Error in video playback:', err);
            toast.error('There was an error playing the video');
        }
    };

    // Handle video ended event
    const handleVideoEnded = (videoId) => {
        if (playingVideoId === videoId) {
            setPlayingVideoId(null);
        }
    };

    return (
        <div className="max-w-6xl mx-auto w-full px-4">
            <div className="flex items-center mb-4 border-b border-white/10 pb-3">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-purple-100/20 text-yellow-300 mr-3">
                    <Video className="w-5 h-5" />
                </div>
                <div className="flex-1">
                    <h2 className="text-xl font-bold text-white">Video Selection</h2>
                    <p className="text-white/80 text-sm">Choose a video for your app's splash screen</p>
                </div>
            </div>

            {/* Two-column layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Left column: Search and Categories */}
                <div className="space-y-4">
                    {/* Search Form */}
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg border border-white/10 p-4 hover:bg-white/20 transition-colors">
                        <div className="space-y-4">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/50 w-5 h-5" />
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    placeholder="Search videos..."
                                    className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-yellow-300/50"
                                />
                            </div>
                            <button
                                onClick={handleSearchSubmit}
                                disabled={!searchTerm.trim() || isSearching}
                                className="w-full px-4 py-2 bg-yellow-300 text-gray-800 rounded-lg hover:bg-yellow-400 transition-colors font-medium flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSearching ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Searching...
                                    </>
                                ) : (
                                    <>
                                        <Search className="w-4 h-4 mr-2" />
                                        Search
                                    </>
                                )}
                            </button>
                        </div>
                        
                        <div className="mt-4">
                            <h3 className="text-sm font-medium text-white mb-2">Suggested Search Terms</h3>
                            <div className="flex flex-wrap gap-2">
                                {contentCategories.slice(0, 9).map(category => (
                                    <button
                                        key={category.name}
                                        onClick={() => handleSuggestedSearch(category.name)}
                                        className="px-3 py-1.5 bg-white/10 border border-yellow-300/30 rounded-full text-white/90 text-xs hover:bg-white/20 hover:text-yellow-300 transition-colors font-medium"
                                        title={category.description || `Search for ${category.name} videos`}
                                    >
                                        {category.name}
                                    </button>
                                ))}
                            </div>
                            
                            {videoSearchTerm && (
                                <div className="mt-3 text-sm text-white/70">
                                    Current search: <span className="text-yellow-300 font-medium">{videoSearchTerm}</span>
                                </div>
                            )}
                        </div>
                    </div>
                    
                    {/* Currently Selected Video */}
                    {selectedVideo && (
                        <div className="bg-white/10 backdrop-blur-sm rounded-lg border border-white/10 p-4 hover:bg-white/20 transition-colors">
                            <h3 className="text-md font-medium text-white mb-3">Selected Video</h3>
                            <div className="flex items-center">
                                <div className="w-20 h-32 rounded overflow-hidden bg-black mr-3">
                                    <img 
                                        src={selectedVideo.thumbnailUrl} 
                                        alt={selectedVideo.title} 
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div className="flex-1">
                                    <p className="text-white text-sm font-medium mb-1">{selectedVideo.title}</p>
                                    <p className="text-white/60 text-xs mb-2">{selectedVideo.author || 'Unknown creator'}</p>
                                    <div className="bg-yellow-300/20 border border-yellow-300/30 text-yellow-300 text-xs py-1 px-2 rounded-full inline-flex items-center">
                                        <Check className="w-3 h-3 mr-1" />
                                        Selected for your app
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Help Info Panel */}
                    <div className="p-3 bg-blue-500/20 border border-blue-500/30 rounded-lg text-white">
                        <div className="flex items-start">
                            <Info className="w-5 h-5 text-blue-300 mr-2 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="text-sm">Select a video to use as your app's splash screen. Click on any video to play it in place.</p>
                                <p className="text-sm mt-1">Try searching different terms to find the perfect video for your app.</p>
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Right column: Video Results */}
                <div className="bg-white/10 backdrop-blur-sm rounded-lg border border-white/10 p-4 hover:bg-white/20 transition-colors h-[calc(100vh-220px)] flex flex-col">
                    <h3 className="text-md font-medium text-white mb-3">Video Results</h3>
                    
                    <div className="flex-1 overflow-y-auto">
                        {isSearching ? (
                            <div className="flex flex-col items-center justify-center h-full">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-300 mb-4"></div>
                                <p className="text-white/80">Searching for videos...</p>
                            </div>
                        ) : error ? (
                            <div className="bg-white/5 border border-white/10 rounded-lg p-4 text-center text-white/70">
                                <p>{error}</p>
                                <button
                                    onClick={() => searchVideos(searchTerm)}
                                    className="mt-3 px-4 py-2 bg-yellow-300 text-gray-800 rounded-lg text-sm hover:bg-yellow-400 transition-colors"
                                >
                                    <RefreshCw className="w-4 h-4 mr-2 inline" />
                                    Try Again
                                </button>
                            </div>
                        ) : searchResults.length > 0 ? (
                            <div className="grid grid-cols-1 gap-4 mt-4">
                                {searchResults.map(video => (
                                    <div 
                                        key={video.id} 
                                        className={`bg-gray-800/70 rounded-lg overflow-hidden border ${
                                            selectedVideo && selectedVideo.id === video.id 
                                            ? 'border-yellow-300 shadow-lg shadow-yellow-300/20' 
                                            : 'border-gray-700 hover:border-white/30'
                                        } transition-all`}
                                    >
                                        <div className="relative">
                                            <div className="aspect-video bg-black flex items-center justify-center">
                                                {playingVideoId === video.id ? (
                                                    <video
                                                        src={video.videoUrl}
                                                        poster={video.thumbnailUrl}
                                                        controls
                                                        autoPlay
                                                        className="w-full h-full object-contain"
                                                        onEnded={() => handleVideoEnded(video.id)}
                                                    />
                                                ) : (
                                                    <>
                                                        <img 
                                                            src={video.thumbnailUrl} 
                                                            alt={video.title}
                                                            className="w-full h-full object-cover"
                                                        />
                                                        <button
                                                            onClick={() => toggleVideoPlay(video)}
                                                            className="absolute inset-0 flex items-center justify-center bg-black/50 hover:bg-black/30 transition-colors"
                                                        >
                                                            <div className="w-16 h-16 rounded-full bg-yellow-300/90 flex items-center justify-center">
                                                                <Play className="w-8 h-8 text-gray-900 ml-1" />
                                                            </div>
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        
                                        <div className="p-4">
                                            <h3 className="text-white font-medium text-lg line-clamp-1">{video.title}</h3>
                                            <p className="text-white/70 text-sm mt-1 line-clamp-2">{video.description}</p>
                                            
                                            <div className="mt-4 flex items-center justify-between">
                                                <div className="text-white/60 text-sm">
                                                    {video.author}
                                                </div>
                                                
                                                <button
                                                    onClick={() => handleVideoSelect(video)}
                                                    className={`px-4 py-2 rounded-md font-medium flex items-center transition-all ${
                                                        selectedVideo && selectedVideo.id === video.id
                                                        ? 'bg-green-500 text-white hover:bg-green-600'
                                                        : 'bg-yellow-300 text-gray-900 hover:bg-yellow-400'
                                                    } shadow-md hover:shadow-lg`}
                                                >
                                                    {selectedVideo && selectedVideo.id === video.id ? (
                                                        <>
                                                            <Check className="w-4 h-4 mr-1" />
                                                            Selected
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Eye className="w-4 h-4 mr-1" />
                                                            Select
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8 text-white/50 h-full flex flex-col items-center justify-center">
                                <Video className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                <p>Search for videos to see results here</p>
                                <p className="text-sm mt-1">Try searching for content relevant to your app</p>
                            </div>
                        )}
                    </div>
                    
                    <div className="mt-4 p-3 bg-blue-500/20 border border-blue-500/30 rounded-lg text-white">
                        <div className="flex items-start">
                            <Info className="w-5 h-5 text-blue-300 mr-2 flex-shrink-0 mt-0.5" />
                            <p className="text-sm">Click on a video to preview it. Once you find the perfect video, click "Select" to use it in your app.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VideoSelectionSlide; 